import React, { useState } from 'react';
import { AlarmasLayout } from '../../components/alarmas/AlarmasLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Button } from '../../components/ui/button';
import { BookOpen, Smartphone, Apple, Bell, Siren, ShieldAlert, MessageCircle, Settings, ChevronRight, Volume2, KeyRound } from 'lucide-react';

// Placeholder para capturas de pantalla (reemplazables luego)
function CapturaPlaceholder({ numero, titulo }) {
    return (
        <div className="my-4 rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50 p-6 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 text-red-600 text-xs font-bold mb-2">
                CAPTURA {numero}
            </div>
            <p className="text-sm text-muted-foreground">{titulo}</p>
        </div>
    );
}

function Paso({ numero, children }) {
    return (
        <div className="flex gap-4 py-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center font-black text-base">
                {numero}
            </div>
            <div className="flex-1 text-base leading-relaxed pt-1">{children}</div>
        </div>
    );
}

export default function AlarmasManualPage() {
    const [active, setActive] = useState('que-es');

    return (
        <AlarmasLayout>
            <div className="max-w-3xl mx-auto space-y-6 animate-fade-in pb-8">
                <div>
                    <h1 className="font-black text-3xl md:text-4xl uppercase tracking-tight flex items-center gap-3" data-testid="manual-title">
                        <BookOpen className="h-8 w-8 text-red-500" />
                        Manual de Usuario
                    </h1>
                    <p className="text-muted-foreground text-base mt-2">
                        Guía paso a paso para usar la app de alarmas. Si necesitas ayuda, pide a un familiar que te acompañe.
                    </p>
                </div>

                <Accordion type="single" collapsible value={active} onValueChange={setActive} className="space-y-3">

                    {/* ¿Qué es esta app? */}
                    <AccordionItem value="que-es" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Siren className="h-5 w-5 text-red-500" />
                                ¿Qué es esta app?
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base space-y-3 pt-2">
                            <p>
                                Esta aplicación te permite <strong>encender o apagar las alarmas (sirenas)</strong> de tu
                                comunidad directamente desde tu celular o computador.
                            </p>
                            <p>
                                Cuando tú o un vecino activa una sirena, todos los usuarios autorizados reciben
                                una <strong>notificación inmediata</strong> en su teléfono.
                            </p>
                            <p>
                                También puedes enviar un <strong>SOS silencioso</strong> si necesitas ayuda sin encender la sirena.
                            </p>
                            <CapturaPlaceholder numero="1" titulo="Pantalla principal con la sirena y botones de acción" />
                        </AccordionContent>
                    </AccordionItem>

                    {/* Instalación Android */}
                    <AccordionItem value="inst-android" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Smartphone className="h-5 w-5 text-emerald-500" />
                                Instalar en Android
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <p className="mb-3">Sigue estos pasos desde tu teléfono Android:</p>
                            <Paso numero="1">Abre <strong>Chrome</strong> y escribe la dirección de la app que te envió el administrador.</Paso>
                            <CapturaPlaceholder numero="2" titulo="Chrome con la dirección de la app abierta" />
                            <Paso numero="2">Ingresa con tu correo y contraseña.</Paso>
                            <Paso numero="3">Toca los <strong>tres puntos</strong> de la esquina superior derecha en Chrome.</Paso>
                            <CapturaPlaceholder numero="3" titulo="Menú de Chrome con los tres puntos resaltados" />
                            <Paso numero="4">Toca <strong>"Instalar app"</strong> o <strong>"Agregar a pantalla principal"</strong>.</Paso>
                            <CapturaPlaceholder numero="4" titulo="Opción Instalar app en el menú" />
                            <Paso numero="5">Confirma tocando <strong>"Instalar"</strong>. Aparecerá un ícono nuevo en tu pantalla.</Paso>
                            <CapturaPlaceholder numero="5" titulo="Ícono instalado en la pantalla del teléfono" />
                            <Paso numero="6">Listo. Ahora abre la app desde ese ícono cada vez que la necesites.</Paso>
                            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                                <strong>Importante:</strong> una vez instalada, continúa con la sección <em>"Activar notificaciones"</em> para recibir las alertas.
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Instalación iPhone */}
                    <AccordionItem value="inst-iphone" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Apple className="h-5 w-5 text-zinc-500" />
                                Instalar en iPhone / iPad
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <p className="mb-3">En iPhone <strong>debes usar Safari</strong> (no funciona con Chrome):</p>
                            <Paso numero="1">Abre <strong>Safari</strong> y escribe la dirección de la app.</Paso>
                            <CapturaPlaceholder numero="6" titulo="Safari abierto con la app" />
                            <Paso numero="2">Ingresa con tu correo y contraseña.</Paso>
                            <Paso numero="3">Toca el ícono de <strong>Compartir</strong> (cuadrado con una flecha hacia arriba, abajo al centro de la pantalla).</Paso>
                            <CapturaPlaceholder numero="7" titulo="Botón compartir en Safari resaltado" />
                            <Paso numero="4">Desliza hacia abajo y toca <strong>"Agregar a pantalla de inicio"</strong>.</Paso>
                            <CapturaPlaceholder numero="8" titulo="Opción Agregar a pantalla de inicio" />
                            <Paso numero="5">Toca <strong>"Agregar"</strong> en la esquina superior derecha.</Paso>
                            <Paso numero="6">Busca el ícono nuevo en tu pantalla principal y tócalo para abrir la app.</Paso>
                            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                                <strong>Muy importante:</strong> en iPhone las notificaciones SOLO funcionan cuando abres la app desde el ícono instalado (no desde Safari). Si abres la app desde Safari, no te llegarán alertas.
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Activar Notificaciones */}
                    <AccordionItem value="activar-notif" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Bell className="h-5 w-5 text-amber-500" />
                                Activar TODAS las notificaciones
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <p className="mb-3">
                                Para que te lleguen las alertas <strong>aunque la app esté cerrada</strong>, sigue estos pasos:
                            </p>
                            <h3 className="font-bold mt-4 mb-2">En la app</h3>
                            <Paso numero="1">Abre la app (desde el ícono instalado).</Paso>
                            <Paso numero="2">Toca el botón <strong>"Activar"</strong> o la campanita en la esquina superior derecha.</Paso>
                            <CapturaPlaceholder numero="10" titulo="Botón Activar notificaciones en el panel principal" />
                            <Paso numero="3">Cuando el navegador pregunte, toca <strong>"Permitir"</strong>.</Paso>
                            <CapturaPlaceholder numero="11" titulo="Diálogo del navegador pidiendo permiso" />

                            <h3 className="font-bold mt-6 mb-2">En Android (además)</h3>
                            <Paso numero="1">Ve a <strong>Ajustes</strong> del teléfono → <strong>Apps</strong>.</Paso>
                            <Paso numero="2">Busca tu app instalada y tócala.</Paso>
                            <Paso numero="3">Entra en <strong>Notificaciones</strong> y asegúrate que TODAS estén activadas.</Paso>
                            <Paso numero="4">Activa <strong>"Mostrar en pantalla completa"</strong> o <strong>"Pop-up"</strong> si aparece la opción (así se ven incluso con el teléfono bloqueado).</Paso>
                            <CapturaPlaceholder numero="12" titulo="Ajustes de notificaciones en Android" />
                            <Paso numero="5">Desactiva <strong>"Ahorro de batería"</strong> para esta app (algunos Android pausan las alertas para ahorrar batería).</Paso>

                            <h3 className="font-bold mt-6 mb-2">En iPhone (además)</h3>
                            <Paso numero="1">Ve a <strong>Ajustes</strong> → <strong>Notificaciones</strong>.</Paso>
                            <Paso numero="2">Busca la app y toca su nombre.</Paso>
                            <Paso numero="3">Activa <strong>Permitir notificaciones</strong>, <strong>Sonidos</strong>, <strong>Insignias</strong> y <strong>Pantalla bloqueada</strong>.</Paso>

                            <h3 className="font-bold mt-6 mb-2">Chat flotante de emergencia</h3>
                            <p>
                                Cuando alguien activa una alarma o un SOS, aparecerá un <strong>chat flotante</strong> en la parte de abajo de tu pantalla.
                                Puedes escribir para coordinarte con los vecinos. El chat se cierra solo después de unos minutos.
                            </p>
                            <CapturaPlaceholder numero="13" titulo="Chat flotante abierto con mensajes de vecinos" />
                            <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm">
                                Si no ves el chat flotante, el administrador puede tenerlo desactivado. Coméntale para que lo active.
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Diagnóstico */}
                    <AccordionItem value="diagnostico" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Bell className="h-5 w-5 text-emerald-500" />
                                Diagnóstico de Notificaciones
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <p>Úsalo para verificar que tu teléfono recibe correctamente las alertas:</p>
                            <Paso numero="1">En el Panel, busca el botón verde <strong>"Diagnóstico de Notificaciones"</strong>.</Paso>
                            <Paso numero="2">Tócalo. La app te enviará una notificación de prueba solo a ti.</Paso>
                            <Paso numero="3">Bloquea tu teléfono unos segundos o minimiza la app para ver si aparece.</Paso>
                            <Paso numero="4">Cuando aparezca la notificación, vuelve a la app y responde las 4 preguntas.</Paso>
                            <Paso numero="5">Si algo falla, el diagnóstico te dirá exactamente qué ajuste del teléfono revisar.</Paso>
                            <CapturaPlaceholder numero="18" titulo="Diálogo de diagnóstico con las 4 preguntas" />
                            <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm">
                                <strong>Recomendado:</strong> haz este diagnóstico al menos una vez después de instalar la app.
                                Así te aseguras de que en una emergencia real sí recibirás las alertas.
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Encender/Apagar Sirena */}
                    <AccordionItem value="sirena" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Siren className="h-5 w-5 text-red-500" />
                                Encender o apagar la sirena
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <Paso numero="1">Abre la app y ve al <strong>Panel</strong>.</Paso>
                            <Paso numero="2">Verás uno o más botones grandes con el ícono de una sirena.</Paso>
                            <CapturaPlaceholder numero="14" titulo="Panel con los botones de sirena" />
                            <Paso numero="3">
                                <strong>Mantén presionado</strong> el botón durante un instante (normalmente menos de 1 segundo).
                                Verás un círculo rojo que se llena. Cuando se complete, la sirena cambia de estado.
                            </Paso>
                            <CapturaPlaceholder numero="15" titulo="Botón con el círculo de progreso al mantener presionado" />
                            <Paso numero="4">Si quieres cancelar, suelta antes de que el círculo se complete.</Paso>
                            <Paso numero="5">
                                <strong>Rojo con sirena pulsando</strong> = encendida. <strong>Gris</strong> = apagada.
                            </Paso>
                            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                                <strong>Por qué mantener presionado:</strong> evita que enciendas la alarma por accidente con un roce.
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* SOS */}
                    <AccordionItem value="sos" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <ShieldAlert className="h-5 w-5 text-orange-500" />
                                Enviar SOS silencioso
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <p className="mb-3">
                                El SOS avisa a los vecinos que necesitas ayuda <strong>SIN encender la sirena</strong>.
                                Útil si hay un intruso y no quieres alertarlo.
                            </p>
                            <Paso numero="1">Ve al Panel.</Paso>
                            <Paso numero="2"><strong>Mantén presionado</strong> el botón naranja <strong>"SOS Silencioso"</strong> unos instantes. Verás una barra blanca que se llena de izquierda a derecha. Suelta solo cuando se complete.</Paso>
                            <CapturaPlaceholder numero="16" titulo="Botón SOS Silencioso con barra de progreso" />
                            <Paso numero="3">Si el teléfono lo permite, autoriza que la app use tu ubicación para que los vecinos sepan dónde estás.</Paso>
                            <Paso numero="4">Listo. Tus vecinos recibirán una notificación con tu nombre y dirección.</Paso>
                            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                                <strong>Igual que la sirena:</strong> el botón SOS también requiere mantener presionado para evitar envíos accidentales.
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Cambiar sonido / contraseña */}
                    <AccordionItem value="config" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <Settings className="h-5 w-5 text-zinc-500" />
                                Cambiar sonido y contraseña
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <h3 className="font-bold mt-2 mb-2 flex items-center gap-2"><Volume2 className="h-4 w-4" /> Sonido de alerta</h3>
                            <Paso numero="1">En el Panel, busca abajo la opción <strong>Sonido</strong>.</Paso>
                            <Paso numero="2">Abre el selector y escoge: Clásico, Urgente, Sirena, Suave o Silencioso.</Paso>
                            <Paso numero="3">El sonido elegido se reproduce de inmediato como prueba.</Paso>

                            <h3 className="font-bold mt-6 mb-2 flex items-center gap-2"><KeyRound className="h-4 w-4" /> Cambiar contraseña</h3>
                            <Paso numero="1">Abre el menú lateral (ícono de 3 rayas arriba a la izquierda).</Paso>
                            <Paso numero="2">En la parte inferior del menú, toca el ícono de <strong>llave</strong>.</Paso>
                            <Paso numero="3">Escribe tu contraseña actual y luego la nueva dos veces.</Paso>
                            <Paso numero="4">Toca <strong>Guardar</strong>.</Paso>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Chat */}
                    <AccordionItem value="chat" className="border-2 rounded-xl px-4 bg-card">
                        <AccordionTrigger className="text-lg font-bold hover:no-underline">
                            <span className="flex items-center gap-3">
                                <MessageCircle className="h-5 w-5 text-blue-500" />
                                Usar el chat flotante
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-base pt-2">
                            <p>El chat aparece automáticamente cuando:</p>
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Alguien enciende o apaga una sirena.</li>
                                <li>Alguien envía un SOS.</li>
                            </ul>
                            <Paso numero="1">Abajo a la derecha verás una burbuja con un mensaje.</Paso>
                            <CapturaPlaceholder numero="17" titulo="Burbuja del chat flotante al aparecer" />
                            <Paso numero="2">Tócala para abrir la conversación.</Paso>
                            <Paso numero="3">Escribe tu mensaje y toca el botón de enviar (flecha).</Paso>
                            <Paso numero="4">El chat se cierra solo después del tiempo configurado por el administrador (por defecto 15 minutos).</Paso>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                <Card className="border-2 border-red-500/30 bg-red-500/5">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-xl">
                            <ChevronRight className="h-5 w-5" /> ¿Necesitas ayuda?
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-base">
                        Si algo no funciona o no entiendes un paso, <strong>pide ayuda al administrador de tu comunidad</strong>.
                        Él puede revisar tu cuenta, reenviar el link de registro o enseñarte personalmente.
                    </CardContent>
                </Card>
            </div>
        </AlarmasLayout>
    );
}
